import './AddConfigModal.scss';
import { useCallback, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  accountKey,
  BillingAccount,
  BillingConfigRow,
  isTVADAccount,
  TVADBillingAccount,
  YouTubeBillingAccount
} from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { Radio } from '@Components/FormCheck';
import { MetricConfig, MetricDropdown, MetricDropdownOption } from '@Components/MetricDropdown';
import { AccountSelect } from './AccountSelect';
import {
  accountLabel,
  AccountOption,
  BILLING_METRIC_OPTIONS,
  CONFIG_ACCOUNT_CATEGORY,
  DSP_SEAT_OPTIONS,
  DspOwnerType,
  getConfigAccountCategory,
  splitAccountsByCategory
} from './billing-utils';

interface ConfigFormState {
  id: string;
  accounts: (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[];
  billingMetric?: string;
  kpi?: { id: number; label: string };
  source?: { id: number; label: string };
  spend?: { type?: string; dspSeatOwner?: string };
}

const EMPTY_FORM: ConfigFormState = {
  id: '',
  accounts: []
};

// Stable signature of the billing-relevant fields, used to detect whether an
// edit actually changed anything (so unedited rows aren't logged as changes).
const configSignature = (config: ConfigFormState): string =>
  JSON.stringify({
    accounts: config.accounts.map(accountKey).sort(),
    billingMetric: config.billingMetric ?? null,
    kpi: config.kpi?.id ?? null,
    source: config.source?.id ?? null,
    spendType: config.spend?.type ?? null,
    dspSeatOwner: config.spend?.dspSeatOwner ?? null
  });

interface DspSeatSectionProps {
  dspSeatOwner: DspOwnerType | undefined;
  onDspSeatOwnerChange: (value: DspOwnerType) => void;
}

const DspSeatSection = ({ dspSeatOwner, onDspSeatOwnerChange }: DspSeatSectionProps) => (
  <div className='add-config-section'>
    <div className='add-config-field'>
      <p className='add-config-label'>Who owns this DSP seat? (TVAD only)</p>
      <div className='add-config-radio-group'>
        {DSP_SEAT_OPTIONS.map(o => (
          <Radio
            key={o.value}
            id={`dsp-seat-${o.value}`}
            name='dspSeatOwner'
            label={o.label}
            value={o.value}
            checked={dspSeatOwner === o.value}
            onChange={() => onDspSeatOwnerChange(o.value as DspOwnerType)}
          />
        ))}
      </div>
    </div>
  </div>
);

interface AddConfigModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (rows: BillingConfigRow[]) => void;
  metricDropdownOptions: {
    kpiOptions: MetricDropdownOption[];
    sourceOptions: MetricDropdownOption[];
  };
  availableAccounts: (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[];
  existingRow?: BillingConfigRow;
}

export const AddConfigModal = ({
  show,
  onClose,
  onSave,
  metricDropdownOptions,
  availableAccounts,
  existingRow
}: AddConfigModalProps) => {
  const [form, setForm] = useState<ConfigFormState>(existingRow ?? EMPTY_FORM);
  const accountCategories = new Set(form.accounts.map(getConfigAccountCategory));
  const hasTVADAccount = accountCategories.has(CONFIG_ACCOUNT_CATEGORY.TVAD);
  const hasMixedAccounts = accountCategories.size > 1;

  // MetricConfig kpi & source are strings; convert from the numeric ids in ConfigFormState
  const metricConfigValue: MetricConfig | null = form.billingMetric
    ? {
        metric: form.billingMetric,
        kpi: form.kpi ? String(form.kpi.id) : null,
        source: form.source ? String(form.source.id) : null,
        customValue: form.spend?.type || null
      }
    : null;

  const handleMetricConfigChange = useCallback(
    (config: MetricConfig) => {
      // MetricDropdown emits string values; convert back to numeric ids for ConfigFormState
      const kpiOption = config.kpi
        ? metricDropdownOptions.kpiOptions.find(o => String(o.value) === config.kpi)
        : undefined;
      const sourceOption = config.source
        ? metricDropdownOptions.sourceOptions.find(o => String(o.value) === config.source)
        : undefined;
      setForm(f => ({
        ...f,
        billingMetric: config.metric,
        kpi: kpiOption ? { id: Number(config.kpi), label: kpiOption.label } : undefined,
        source: sourceOption ? { id: Number(config.source), label: sourceOption.label } : undefined,
        spend: config.customValue
          ? { type: config.customValue, dspSeatOwner: f.spend?.dspSeatOwner }
          : undefined
      }));
    },
    [metricDropdownOptions]
  );

  const handleClose = useCallback(() => {
    setForm(existingRow ?? EMPTY_FORM);
    onClose();
  }, [existingRow, onClose]);

  const handleSave = useCallback(() => {
    const buckets = splitAccountsByCategory(form.accounts);

    const rows: BillingConfigRow[] = [];
    if (buckets[CONFIG_ACCOUNT_CATEGORY.TVAD].length > 0) {
      rows.push({ ...form, accounts: buckets[CONFIG_ACCOUNT_CATEGORY.TVAD] } as BillingConfigRow);
    }
    if (buckets[CONFIG_ACCOUNT_CATEGORY.YOUTUBE].length > 0) {
      rows.push({
        ...form,
        accounts: buckets[CONFIG_ACCOUNT_CATEGORY.YOUTUBE],
        spend: form.spend ? { type: form.spend.type } : undefined
      } as BillingConfigRow);
    }
    if (buckets[CONFIG_ACCOUNT_CATEGORY.OTHER].length > 0) {
      rows.push({
        ...form,
        accounts: buckets[CONFIG_ACCOUNT_CATEGORY.OTHER],
        spend: form.spend ? { type: form.spend.type } : undefined
      } as BillingConfigRow);
    }

    onSave(rows);
    handleClose();
  }, [form, handleClose, onSave]);

  const handleAccountsChange = useCallback(
    (accounts: (BillingAccount | TVADBillingAccount | YouTubeBillingAccount)[]) => {
      setForm(f => {
        const hasTvad = accounts.some(isTVADAccount);
        const spend = hasTvad || !f.spend ? f.spend : { type: f.spend.type };
        return { ...f, accounts, spend };
      });
    },
    []
  );

  const isEdit = existingRow !== undefined;
  const isSaveDisabled =
    form.accounts.length === 0 ||
    !form.billingMetric ||
    (!(form.kpi && form.source) && !form.spend?.type) ||
    // Editing a row that wasn't actually changed shouldn't stage a no-op change.
    (existingRow !== undefined && configSignature(form) === configSignature(existingRow));

  const accountOptions: AccountOption[] = availableAccounts.map(a => ({
    value: accountKey(a),
    label: accountLabel(a),
    account: a
  }));

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className='add-config-modal'
      centered
      // MetricDropdown renders its search input via a portal to document.body — outside
      // the modal DOM. Bootstrap's enforceFocus would pull focus back on every keystroke,
      // breaking the search bar. Disable it to allow portal-rendered inputs to work.
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <div>
          <h2 className='add-config-title'>
            {isEdit ? 'Edit Billing Metric for Account(s)' : 'Set Billing Metric for Account(s)'}
          </h2>
        </div>
      </Modal.Header>
      <Modal.Body>
        <AccountSelect
          options={accountOptions}
          value={form.accounts}
          onChange={handleAccountsChange}
        />
        {hasMixedAccounts && (
          <p className='add-config-helper-text add-config-mixed-notice'>
            TVAD, YouTube, and other accounts will be saved as separate rows.
          </p>
        )}
        <hr className='add-config-divider' />
        <div className='add-config-field'>
          <p className='add-config-label'>
            Billing Metric<span className='billing-asterisk'>*</span>
          </p>
          <MetricDropdown
            value={metricConfigValue}
            metrics={BILLING_METRIC_OPTIONS}
            kpis={metricDropdownOptions.kpiOptions}
            sources={metricDropdownOptions.sourceOptions}
            optionalPane='source'
            metricPaneTitle='Billing Metric'
            kpiPaneTitle='KPI Name'
            optionalPaneTitle='KPI Sources'
            onChange={handleMetricConfigChange}
            menuClassName='add-config-metric-dropdown'
            showSearchBar
            showPrettyKpiLabel
          />
          <p className='add-config-helper-text'>
            This determines how spend and performance data are calculated.
          </p>
        </div>
        {hasTVADAccount && form.billingMetric === 'spend' && (
          <>
            <hr className='add-config-divider' />
            <DspSeatSection
              dspSeatOwner={form.spend?.dspSeatOwner as DspOwnerType | undefined}
              onDspSeatOwnerChange={dspSeatOwner =>
                setForm(f => ({ ...f, spend: { ...f.spend, dspSeatOwner } }))
              }
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaveDisabled}>
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
